import type { ApiResponse } from '../types';

export const API_BASE = import.meta.env.PROD
  ? 'https://novel-api.ruei2studio.com/api'
  : '/api';

let authFailureHandler: (() => void) | null = null;

export function setAuthFailureHandler(handler: () => void) {
  authFailureHandler = handler;
}

/**
 * Refresh access token using refresh token cookie
 */
async function refreshToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Core request function with automatic token refresh
 */
export async function request<T>(
  url: string,
  options?: RequestInit,
  isRetry = false
): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  // Handle 401 by attempting token refresh (only once)
  if (response.status === 401 && !isRetry) {
    const refreshed = await refreshToken();
    if (refreshed) {
      return request<T>(url, options, true);
    }
    authFailureHandler?.();
  }

  // Handle non-OK responses before parsing JSON
  if (!response.ok) {
    // Try to parse error response as JSON, fallback to status text
    let errorMessage: string;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
    } catch {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data: ApiResponse<T> = await response.json();

  if (!data.success) {
    throw new Error(data.error || `API Error: HTTP ${response.status} ${response.statusText}`);
  }

  return data.data as T;
}

/**
 * Request with FormData (for file uploads)
 */
export async function requestFormData<T>(
  url: string,
  formData: FormData,
  isRetry = false
): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  // Handle 401 by attempting token refresh (only once)
  if (response.status === 401 && !isRetry) {
    const refreshed = await refreshToken();
    if (refreshed) {
      return requestFormData<T>(url, formData, true);
    }
    authFailureHandler?.();
  }

  // Handle non-OK responses before parsing JSON
  if (!response.ok) {
    // Try to parse error response as JSON, fallback to status text
    let errorMessage: string;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
    } catch {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data: ApiResponse<T> = await response.json();

  if (!data.success) {
    throw new Error(data.error || `API Error: HTTP ${response.status} ${response.statusText}`);
  }

  return data.data as T;
}
